// Carrying a destination across the login screen.
//
// Why this exists (thread #1727, msg 1a16084ba663eb0c): a notification's
// "Open" is a full-page load of a protected route. When the browsing context
// has no portal session — a push notification opens a fresh context, or iOS
// Safari evicted localStorage — the app sends the user to the login screen and
// then, on success, hard-navigates to /home. The Event Chat they clicked is
// gone: they have to go find the event themselves.
//
// So the auth boundary now carries the destination in ?next=, and the two
// places that decide "where does a logged-in user land" read it instead of
// hard-coding /home.

// Anything reaching the router from a URL is attacker-controllable. A ?next=
// that the browser would treat as an absolute URL turns the login screen into
// an open redirect, so only in-app paths survive:
//   "https://evil.com"   rejected — not a path
//   "//evil.com"         rejected — protocol-relative
//   "/\evil.com"         rejected — browsers normalise the backslash to //
//   "javascript:alert(1)" rejected — no leading slash
//   "/manage-event/x?chat=1"  kept
export const sanitizeNextPath = (raw) => {
  if (typeof raw !== "string") return null;
  if (raw.length === 0 || raw.length > 2000) return null;
  if (raw[0] !== "/") return null;
  if (raw[1] === "/" || raw[1] === "\\") return null;
  // Control characters can smuggle a second path past some proxies/log readers.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  return raw;
};

// The url a protected route sends a sessionless visitor to. Pass the router's
// own location object. A visit to the login page itself carries no destination
// — otherwise logging in from "/" would send the user to "/" again.
export const loginPathFor = (location) => {
  if (!location) return "/";
  // "/" is the login page itself, so it is never a destination: carrying it
  // would send the user from / back to / on a successful login.
  const pathname = location.pathname;
  if (!pathname || pathname === "/") return "/";
  const target = `${pathname}${location.search || ""}${location.hash || ""}`;
  if (!sanitizeNextPath(target)) return "/";
  return `/?next=${encodeURIComponent(target)}`;
};

// Read the destination back off the login page. Returns null when there is
// none, so callers fall back to the normal landing page.
export const nextPathFromSearch = (search) => {
  if (!search) return null;
  try {
    return sanitizeNextPath(new URLSearchParams(search).get("next"));
  } catch (err) {
    return null;
  }
};
